import { ICurrencyResp } from '@monetize/types/app';
import { useEffect, useMemo, useState } from 'react';
import { useGetCurrencySettings } from '../../../api/settingsService';
import {
  MBox,
  MCard,
  MFlex,
  MPageContainer,
  MPageLoader,
  MSearchInput,
  MSettingsPageHeader,
  MSimpleGrid,
  MText,
  MVStack,
} from '../../../components/Monetize';
import { useDocumentHead } from '../../../services/documentHead';
import { AvailableCurrencyTile } from './components/AvailableCurrencyTile';
import { EnabledCurrencyRow } from './components/EnabledCurrencyRow';
import {
  getAvailableCurrencyCodes,
  matchesCurrencySearch,
} from './utils/currencySettings.utils';

const EMPTY_CURRENCIES: ICurrencyResp[] = [];

const SectionHeading = ({
  title,
  count,
  description,
}: {
  title: string;
  count: number;
  description: string;
}) => (
  <MBox>
    <MFlex align="baseline" gap={2}>
      <MText fontSize="md" fontWeight="semibold" color="fg.strong">
        {title}
      </MText>
      <MText fontSize="sm" color="fg.muted">
        {count}
      </MText>
    </MFlex>
    <MText fontSize="sm" color="fg.muted">
      {description}
    </MText>
  </MBox>
);

const CurrencySettings = () => {
  const { setDocTitle } = useDocumentHead();
  const [searchTerm, setSearchTerm] = useState('');
  const [isSettingDefault, setIsSettingDefault] = useState(false);

  const { isLoading, data } = useGetCurrencySettings();
  const enabledCurrencies = data?.content ?? EMPTY_CURRENCIES;

  const availableCodes = useMemo(
    () => getAvailableCurrencyCodes(enabledCurrencies),
    [enabledCurrencies],
  );
  const matchingAvailableCodes = useMemo(
    () =>
      new Set(
        availableCodes.filter((code) =>
          matchesCurrencySearch(code, searchTerm),
        ),
      ),
    [availableCodes, searchTerm],
  );

  useEffect(() => {
    setDocTitle('Settings', 'Currencies');
  }, []);

  return (
    <MPageContainer data-testid="currency-settings-page" alignItems="stretch">
      <MBox>
        <MSettingsPageHeader title="Currencies" />
      </MBox>
      {isLoading ? (
        <MPageLoader />
      ) : (
        <MVStack align="stretch" gap={8} maxW="960px" pb={8}>
          <MVStack align="stretch" gap={3}>
            <SectionHeading
              title="Enabled"
              count={enabledCurrencies.length}
              description="Currencies available on accounts, products, quotes, and invoices. New records use the default currency."
            />
            <MCard variant="border" overflow="hidden">
              {enabledCurrencies.map((currency) => (
                <EnabledCurrencyRow
                  key={currency.code}
                  currency={currency}
                  isSettingDefaultDisabled={isSettingDefault}
                  onSettingDefaultChange={setIsSettingDefault}
                />
              ))}
            </MCard>
          </MVStack>

          {availableCodes.length > 0 && (
            <MVStack align="stretch" gap={3}>
              <MFlex
                justify="space-between"
                align={{ base: 'stretch', md: 'flex-end' }}
                direction={{ base: 'column', md: 'row' }}
                gap={3}
              >
                <SectionHeading
                  title="Not enabled"
                  count={availableCodes.length}
                  description="Enable a currency to start pricing and billing in it."
                />
                <MBox w={{ base: 'full', md: '280px' }} flexShrink={0}>
                  <MSearchInput
                    data-testid="currency-search"
                    aria-label="Search currencies"
                    placeholder="Search by code or name"
                    value={searchTerm}
                    onChange={setSearchTerm}
                    omitCount
                  />
                </MBox>
              </MFlex>
              {/* Hide rather than unmount non-matches so an in-flight Enable keeps its busy state while filtering */}
              <MSimpleGrid minChildWidth="260px" gap={2}>
                {availableCodes.map((code) => (
                  <AvailableCurrencyTile
                    key={code}
                    code={code}
                    isHidden={!matchingAvailableCodes.has(code)}
                  />
                ))}
              </MSimpleGrid>
              {matchingAvailableCodes.size === 0 && (
                <MText fontSize="sm" color="fg.muted" py={4} textAlign="center">
                  No currencies match “{searchTerm.trim()}”.
                </MText>
              )}
            </MVStack>
          )}
        </MVStack>
      )}
    </MPageContainer>
  );
};

export default CurrencySettings;
